
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Copy, Share, QrCode, Mail } from "lucide-react";
import { FaWhatsapp, FaTelegram } from "react-icons/fa";
import { webUrl, isNativeBuild } from "@/lib/config";

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  inviteCode: string;
  roomName: string;
}

export function InviteModal({
  isOpen,
  onClose,
  inviteCode,
  roomName,
}: InviteModalProps) {
  const [qrCode, setQrCode] = useState<string>("");
  const { toast } = useToast();

  // Always the public web origin — an invite pointing at the WebView's own
  // origin (capacitor://localhost) is unopenable for whoever receives it.
  const inviteUrl = webUrl(`/app/join/${inviteCode}`);

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast({
        title: "Link copied!",
        description: "Invite link has been copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy link to clipboard",
        variant: "destructive",
      });
    }
  };

  const shareViaWhatsApp = () => {
    const message = `Join me on Vyzle Chat: ${roomName} \n${inviteUrl} `;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

   const shareViaTelegram = () => {
    const message = `Join me on Vyzle Chat: ${roomName}\n${inviteUrl}`;
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(inviteUrl)}&text=${encodeURIComponent(`Join me on Vyzle Chat: ${roomName}`)}`;



    window.open(telegramUrl, "_blank");
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(
      `Join me on Vyzle Chat: ${roomName}`
    );
    const body = encodeURIComponent(
      `Hey!\n\nI'd like you to join my chat "${roomName}" on Vyzle. Click the link below to join:\n\n${inviteUrl}\n\nSee you there!`
    );
    // _blank so the mail composer doesn't unload the chat tab on mobile
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  const shareNative = async () => {
    const title = "Join Vyzle Chat";
    const text = `Join me on Vyzle Chat: ${roomName}`;

    // In the native shell `navigator.share` is unavailable on iOS's
    // capacitor:// scheme, so without this every share would quietly degrade
    // to a clipboard copy — the one action a native app is expected to do well.
    if (isNativeBuild) {
      try {
        const { Share } = await import("@capacitor/share");
        await Share.share({ title, text, url: inviteUrl, dialogTitle: title });
      } catch (error) {
        // A cancelled share sheet rejects too, so don't treat this as failure
        // loud enough to bother the user — just leave the modal as it was.
        console.log("Share cancelled or failed");
      }
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: inviteUrl });
      } catch (error) {
        console.log("Share cancelled or failed");
      }
    } else {
      copyInviteLink();
    }
  };

  const generateQRCode = () => {
    const qrSize = 200;
    const qrData = encodeURIComponent(inviteUrl);
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${qrData}`;
    setQrCode(qrUrl);
  };

  useEffect(() => {
    if (isOpen && inviteCode) {
      generateQRCode();
    }
  }, [isOpen, inviteCode]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95%] sm:max-w-md rounded-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-base sm:text-lg">
            <Share className="w-5 h-5 text-[var(--bubble-own-to)]" />
            <span className="truncate">Invite to {roomName}</span>
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Share the link or QR code — anyone who opens it will join this chat.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* QR Code */}
          <div className="text-center">
            <div className="w-full max-w-[200px] aspect-square mx-auto bg-muted rounded-lg flex items-center justify-center mb-4">
              {qrCode ? (
                <img
                  src={qrCode}
                  alt="QR Code"
                  className="w-[80%] h-[80%]"
                />
              ) : (
                <div className="text-center px-2">
                  <QrCode className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    Generating QR code...
                  </p>
                </div>
              )}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Scan to join the conversation
            </p>
          </div>

          {/* Invite Link */}
          <div className="space-y-4">
            {/* Input + Copy */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={inviteUrl}
                readOnly
                className="flex-1 bg-muted text-xs sm:text-sm"
              />
              <Button
                onClick={copyInviteLink}
                className="bg-[var(--accent)] w-full sm:w-auto text-white flex items-center justify-center gap-2"
              >
                <Copy className="w-4 h-4" />
                <span>Copy</span>
              </Button>
            </div>

            {/* Share Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Button
                onClick={shareViaWhatsApp}
                className="bg-[hsl(135,71%,57%)] hover:bg-[hsl(135,71%,52%)] text-white"
              >
                <FaWhatsapp className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>

              <Button
                onClick={shareViaTelegram}
                className="bg-[hsl(207,90%,54%)] hover:bg-[hsl(207,90%,49%)] text-white"
              >
                <FaTelegram className="w-4 h-4 mr-2" />
                Telegram
              </Button>

              <Button
                onClick={shareViaEmail}
                className="bg-gray-600 hover:bg-gray-700 text-white"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email
              </Button>
            </div>

            {/* Native Share */}
            <Button
              onClick={shareNative}
              variant="secondary"
              className="w-full"
            >
              <Share className="w-4 h-4 mr-2" />
              Share Link
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

}